import styles from './styles.module.scss';
import moment from 'moment';
import { useMemo } from 'react';
import { UsersIcon } from '../../IntelAgent/components/Icons';
import { convertNameToAvatarPlaceholder } from 'helpers';
import { Avatar } from 'components';
import { PreparedAgentIntelActivityDataType } from 'types/intel';
import { ImageGrid } from '../NeighborhoodTab/components/ImageGrid';
import { getYouTubeThumbnail } from 'utils';

export interface ActivityCardProps {
  activity: PreparedAgentIntelActivityDataType;
  forClient?: boolean;
}

export const ActivityCard = (props: ActivityCardProps) => {
  const { activity, forClient } = props;

  const publishedToText = useMemo(() => {
    if (activity.publishedTo === 'ALL') {
      return 'Published to all clients';
    }

    if (activity.publishedTo.type === 'CLIENT') {
      const clients = activity.publishedTo.data.slice(0, 3).join(', ');
      return activity.publishedTo.data.length > 3
        ? `${clients} +${activity.publishedTo.data.length - 3}`
        : clients;
    }

    const areas = activity.publishedTo.data.slice(0, 3).join('; ');
    return activity.publishedTo.data.length > 3
      ? `Published to clients in: ${areas} +${activity.publishedTo.data.length - 3}`
      : `Published to clients in: ${areas}`;
  }, [activity.publishedTo]);

  const getImagesUrls = (): string[] => {
    let urls: string[] = [];

    if (activity.images.length) urls = [...activity.images];

    if (activity.mediaLink) {
      const thumbnail = getYouTubeThumbnail(activity.mediaLink);
      if (thumbnail) urls.unshift(thumbnail);
    }

    return urls;
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.profile}>
          <Avatar
            className={styles.avatarWrap}
            avatarClassName={styles.avatar}
            src={activity.user.avatar}
            placeholder={convertNameToAvatarPlaceholder(activity.user.name)}
          />
          <div className={styles.profileName}>{activity.user.name}</div>
          <div className={styles.profileRole}>{activity.user.role}</div>
        </div>
        <div className={styles.timeFromNow}>
          {activity?.isNew ? 'Just now' : moment(activity.createdAt).fromNow()}
        </div>
      </div>
      <div className={styles.info}>
        <div className={styles.title}>{activity.title}</div>
        <div className={styles.description}>{activity.description}</div>
        {activity.mediaLink && (
          <a
            target="_blank"
            href={activity.mediaLink}
            className={styles.mediaLink}
            rel="noreferrer"
          >
            {activity.mediaLink}
          </a>
        )}
      </div>

      {activity.images.length > 0 || activity.mediaLink ? (
        <ImageGrid
          images={getImagesUrls()}
          heading={activity.title}
          thumbnailLink={activity.mediaLink}
        />
      ) : null}

      {forClient ? null : (
        <div className={styles.publishedTo}>
          <UsersIcon />
          <div className={styles.text}>{publishedToText}</div>
        </div>
      )}
    </div>
  );
};
